import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ImageIcon, Link2, Loader2, Play, RotateCw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { PinterestLogo } from './PinterestLogo';
import {
    PinterestBoard,
    PinterestPage,
    PinterestPin,
    importPinAsFile,
    isPinterestConnectionError,
    usePinterestApi,
    usePinterestStatus,
} from './use-pinterest';

export interface PinterestPickMeta {
    kind: 'image' | 'video';
    title?: string;
    /** The pin's page on Pinterest — provenance for whatever the file becomes. */
    sourceUrl?: string;
    width?: number;
    height?: number;
}

export interface PinterestPickerPanelProps {
    open: boolean;
    onClose: () => void;
    /**
     * A real File, already copied to our storage and read back, so the caller
     * can treat it exactly like a dropped file. The panel closes once this resolves.
     */
    onPick: (file: File, meta: PinterestPickMeta) => void | Promise<void>;
}

/** pinterest.com/pin/…, a localised pinterest.xx/pin/…, a pin.it short link, or a bare pin id. */
const PIN_LINK = /(^|\/\/|\.)(pinterest\.[a-z.]{2,6}\/pin\/|pin\.it\/)|^\s*\d{6,}\s*$/i;

type Tab = 'boards' | 'pins';

interface PagedState<T> {
    items: T[];
    hasMore: boolean;
    loading: boolean;
    loadingMore: boolean;
    error: any;
    loadMore: () => void;
    reload: () => void;
}

/**
 * One paginated Pinterest list. Resets whenever `key` changes, and only
 * fetches while `enabled` — the panel stays mounted while closed.
 */
function usePaged<T>(
    key: string,
    enabled: boolean,
    fetchPage: (bookmark?: string) => Promise<PinterestPage<T>>,
): PagedState<T> {
    const [items, setItems] = useState<T[]>([]);
    const [bookmark, setBookmark] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<any>(null);
    const [loadedKey, setLoadedKey] = useState<string | null>(null);
    const fetchRef = useRef(fetchPage);
    fetchRef.current = fetchPage;

    const load = useCallback(async (k: string) => {
        setLoading(true);
        setError(null);
        setItems([]);
        setBookmark(undefined);
        try {
            const page = await fetchRef.current();
            setItems(page.items || []);
            setBookmark(page.bookmark);
            setLoadedKey(k);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (enabled && loadedKey !== key) load(key);
    }, [enabled, key, loadedKey, load]);

    const loadMore = useCallback(async () => {
        if (!bookmark || loadingMore) return;
        setLoadingMore(true);
        try {
            const page = await fetchRef.current(bookmark);
            setItems((prev) => [...prev, ...(page.items || [])]);
            setBookmark(page.bookmark);
        } catch (err) {
            setError(err);
        } finally {
            setLoadingMore(false);
        }
    }, [bookmark, loadingMore]);

    return { items, hasMore: Boolean(bookmark), loading, loadingMore, error, loadMore, reload: () => load(key) };
}

/**
 * Pick any Pinterest pin — a picture or a video — as a Studio reference.
 *
 * The link field always works (it needs no account and no app credentials);
 * below it, a connected vendor browses their boards, and anyone else is
 * offered the connection, or told plainly that it is not available yet.
 */
export const PinterestPickerPanel: React.FC<PinterestPickerPanelProps> = ({ open, onClose, onPick }) => {
    const { t } = useTranslation();
    const api = usePinterestApi();
    const pinterest = usePinterestStatus();
    const { refresh } = pinterest;

    const [tab, setTab] = useState<Tab>('boards');
    const [board, setBoard] = useState<PinterestBoard | null>(null);
    const [link, setLink] = useState('');
    const [linkError, setLinkError] = useState<string | null>(null);
    /** The pin being brought in ('link' for the pasted one) — everything else waits. */
    const [busy, setBusy] = useState<string | null>(null);
    const [pickError, setPickError] = useState<string | null>(null);
    const [connectError, setConnectError] = useState<string | null>(null);

    // Opened again later: the connection may have changed in another tab.
    useEffect(() => {
        if (open) refresh();
    }, [open, refresh]);

    const canBrowse = open && pinterest.connected;
    const boards = usePaged<PinterestBoard>('boards', canBrowse && tab === 'boards' && !board, (b) => api.listBoards(b));
    const pins = usePaged<PinterestPin>(
        board ? `board:${board.id}` : 'mine',
        canBrowse && (tab === 'pins' || Boolean(board)),
        (b) => (board ? api.listBoardPins(board.id, b) : api.listMyPins(b)),
    );

    // A browse that finds the token dead means the status is stale: re-read it.
    useEffect(() => {
        if (isPinterestConnectionError(boards.error) || isPinterestConnectionError(pins.error)) refresh();
    }, [boards.error, pins.error, refresh]);

    const pick = async (pin: PinterestPin, busyKey: string) => {
        setBusy(busyKey);
        setPickError(null);
        setLinkError(null);
        try {
            const { file, imported } = await importPinAsFile(api, pin);
            await onPick(file, {
                kind: imported.kind,
                title: pin.title,
                sourceUrl: pin.pinUrl,
                width: imported.width,
                height: imported.height,
            });
            setLink('');
            onClose();
        } catch (err: any) {
            const msg = /stream|larger than/i.test(err?.message || '')
                ? /larger than/i.test(err?.message) ? t('PINTEREST.ERROR_TOO_LARGE') : t('PINTEREST.ERROR_STREAM_ONLY')
                : t('PINTEREST.ERROR_PICK');
            if (busyKey === 'link') setLinkError(msg);
            else setPickError(msg);
        } finally {
            setBusy(null);
        }
    };

    const submitLink = async (e: React.FormEvent) => {
        e.preventDefault();
        const value = link.trim();
        if (!value || busy) return;
        if (!PIN_LINK.test(value)) {
            setLinkError(t('PINTEREST.ERROR_NOT_A_PIN_LINK'));
            return;
        }
        setBusy('link');
        setLinkError(null);
        let pin: PinterestPin;
        try {
            pin = await api.resolve(value);
        } catch {
            setLinkError(t('PINTEREST.ERROR_RESOLVE'));
            setBusy(null);
            return;
        }
        await pick(pin, 'link');
    };

    const startConnect = async () => {
        setConnectError(null);
        try {
            await pinterest.connect();
        } catch {
            setConnectError(t('PINTEREST.ERROR_CONNECT'));
        }
    };

    const close = () => {
        if (busy) return;
        setPickError(null);
        setLinkError(null);
        onClose();
    };

    return (
        <Sheet open={open} onOpenChange={(next) => { if (!next) close(); }}>
            <SheetContent side="right" className="gap-0 p-0 text-sm data-[side=right]:w-full data-[side=right]:sm:max-w-[560px]" data-testid="pinterest-picker">
                <SheetHeader className="gap-1 border-b p-5 pe-12">
                    <div className="flex items-center gap-2">
                        <PinterestLogo size={20} />
                        <SheetTitle className="text-base font-medium">{t('PINTEREST.TITLE')}</SheetTitle>
                    </div>
                    <SheetDescription className="text-sm">{t('PINTEREST.SUBTITLE')}</SheetDescription>
                </SheetHeader>

                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                    {/* ── Paste a link — always available ─────────────── */}
                    <form onSubmit={submitLink} className="flex flex-col gap-1.5 border-b p-5" data-testid="pinterest-link-form">
                        <label htmlFor="pinterest-link" className="text-xs font-medium text-muted-foreground">
                            {t('PINTEREST.LINK_LABEL')}
                        </label>
                        <div className="flex gap-2">
                            <div className="relative min-w-0 flex-1">
                                <Link2 className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    id="pinterest-link"
                                    value={link}
                                    onChange={(e) => { setLink(e.target.value); setLinkError(null); }}
                                    placeholder={t('PINTEREST.LINK_PLACEHOLDER')}
                                    className="ps-8"
                                    dir="ltr"
                                    disabled={Boolean(busy)}
                                    aria-invalid={Boolean(linkError)}
                                    autoComplete="off"
                                />
                            </div>
                            <Button type="submit" variant="outline" disabled={!link.trim() || Boolean(busy)}>
                                {busy === 'link' && <Loader2 className="animate-spin" />}
                                {t('PINTEREST.LINK_SUBMIT')}
                            </Button>
                        </div>
                        <p className={cn('text-xs', linkError ? 'text-destructive' : 'text-muted-foreground')} role={linkError ? 'alert' : undefined}>
                            {linkError || (busy === 'link' ? t('PINTEREST.IMPORTING') : t('PINTEREST.LINK_HINT'))}
                        </p>
                    </form>

                    {/* ── Browse — or why not ─────────────────────────── */}
                    <div className="flex flex-1 flex-col gap-4 p-5">
                        {pinterest.loading && !pinterest.status ? (
                            <BoardSkeleton />
                        ) : !pinterest.configured ? (
                            <Notice
                                title={t('PINTEREST.UNCONFIGURED_TITLE')}
                                body={t('PINTEREST.UNCONFIGURED_BODY')}
                                testId="pinterest-unconfigured"
                            />
                        ) : !pinterest.connected ? (
                            <Notice
                                title={pinterest.expired ? t('PINTEREST.EXPIRED_TITLE') : t('PINTEREST.CONNECT_TITLE')}
                                body={pinterest.expired ? t('PINTEREST.EXPIRED_BODY') : t('PINTEREST.CONNECT_BODY')}
                                testId="pinterest-connect"
                                action={
                                    <Button variant="outline" onClick={startConnect} disabled={pinterest.redirecting}>
                                        {pinterest.redirecting ? <Loader2 className="animate-spin" /> : <PinterestLogo size={16} />}
                                        {pinterest.redirecting
                                            ? t('PINTEREST.CONNECTING')
                                            : pinterest.expired ? t('PINTEREST.RECONNECT') : t('PINTEREST.CONNECT')}
                                    </Button>
                                }
                                error={connectError}
                            />
                        ) : (
                            <>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    {board ? (
                                        <div className="flex min-w-0 items-center gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => setBoard(null)} disabled={Boolean(busy)}>
                                                <ArrowLeft className="rtl:rotate-180" />
                                                {t('PINTEREST.BACK_TO_BOARDS')}
                                            </Button>
                                            <span className="truncate font-medium">{board.name}</span>
                                        </div>
                                    ) : (
                                        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
                                            <TabsList>
                                                <TabsTrigger value="boards" className="px-3">{t('PINTEREST.TAB_BOARDS')}</TabsTrigger>
                                                <TabsTrigger value="pins" className="px-3">{t('PINTEREST.TAB_PINS')}</TabsTrigger>
                                            </TabsList>
                                        </Tabs>
                                    )}
                                    {pinterest.status?.account && (
                                        <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                                            {pinterest.status.account.avatar && (
                                                <img src={pinterest.status.account.avatar} alt="" className="size-4 rounded-full" />
                                            )}
                                            <span className="truncate" dir="ltr">@{pinterest.status.account.username}</span>
                                        </span>
                                    )}
                                </div>

                                {pickError && (
                                    <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" role="alert">
                                        {pickError}
                                    </p>
                                )}

                                {tab === 'boards' && !board ? (
                                    <BoardList
                                        state={boards}
                                        onOpen={(b) => { setPickError(null); setBoard(b); }}
                                        disabled={Boolean(busy)}
                                    />
                                ) : (
                                    <PinList
                                        state={pins}
                                        busy={busy}
                                        onPick={(pin) => pick(pin, pin.id)}
                                        emptyTitle={board ? t('PINTEREST.EMPTY_BOARD_TITLE') : t('PINTEREST.EMPTY_PINS_TITLE')}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

// ── Pieces ────────────────────────────────────────────────────────

const Notice: React.FC<{ title: string; body: string; action?: React.ReactNode; error?: string | null; testId?: string }> = ({
    title,
    body,
    action,
    error,
    testId,
}) => (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-10 text-center" data-testid={testId}>
        <span className="flex size-10 items-center justify-center rounded-xl bg-muted">
            <PinterestLogo size={22} />
        </span>
        <div className="flex max-w-xs flex-col gap-1">
            <p className="font-medium text-foreground">{title}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
        </div>
        {action}
        {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
    </div>
);

const ListError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
    const { t } = useTranslation();
    return (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-10 text-center" role="alert">
            <p className="text-sm text-muted-foreground">{t('PINTEREST.ERROR_LOAD')}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
                <RotateCw />
                {t('PINTEREST.RETRY')}
            </Button>
        </div>
    );
};

const EmptyList: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed px-6 py-10 text-center">
        <p className="font-medium text-foreground">{title}</p>
        <p className="max-w-xs text-sm text-muted-foreground">{body}</p>
    </div>
);

const LoadMore: React.FC<{ show: boolean; loading: boolean; onClick: () => void }> = ({ show, loading, onClick }) => {
    const { t } = useTranslation();
    if (!show) return null;
    return (
        <div className="flex justify-center pt-1">
            <Button variant="outline" size="sm" onClick={onClick} disabled={loading}>
                {loading && <Loader2 className="animate-spin" />}
                {t('PINTEREST.LOAD_MORE')}
            </Button>
        </div>
    );
};

const BoardSkeleton: React.FC = () => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-hidden>
        {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
                <Skeleton className="aspect-square w-full rounded-lg" />
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
            </div>
        ))}
    </div>
);

/** Heights vary like the pins they stand in for, so the grid does not jump when they arrive. */
const PIN_SKELETON_HEIGHTS = ['h-40', 'h-56', 'h-48', 'h-64', 'h-44', 'h-52', 'h-36', 'h-60', 'h-48'];

const PinSkeleton: React.FC = () => (
    <div className="columns-2 gap-2 sm:columns-3" aria-hidden>
        {PIN_SKELETON_HEIGHTS.map((h, i) => (
            <Skeleton key={i} className={cn('mb-2 w-full break-inside-avoid rounded-lg', h)} />
        ))}
    </div>
);

const BoardList: React.FC<{ state: PagedState<PinterestBoard>; onOpen: (b: PinterestBoard) => void; disabled: boolean }> = ({
    state,
    onOpen,
    disabled,
}) => {
    const { t } = useTranslation();
    if (state.loading) return <BoardSkeleton />;
    if (state.error && !state.items.length) {
        return isPinterestConnectionError(state.error) ? <BoardSkeleton /> : <ListError onRetry={state.reload} />;
    }
    if (!state.items.length) return <EmptyList title={t('PINTEREST.EMPTY_BOARDS_TITLE')} body={t('PINTEREST.EMPTY_BOARDS_BODY')} />;

    return (
        <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="pinterest-boards">
                {state.items.map((b) => (
                    <button
                        key={b.id}
                        type="button"
                        onClick={() => onOpen(b)}
                        disabled={disabled}
                        className="group flex min-w-0 flex-col gap-1.5 rounded-lg text-start outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60"
                    >
                        <span className="block aspect-square w-full overflow-hidden rounded-lg border bg-muted">
                            {b.coverUrl ? (
                                <img
                                    src={b.coverUrl}
                                    alt=""
                                    loading="lazy"
                                    className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                />
                            ) : (
                                <span className="flex size-full items-center justify-center text-muted-foreground">
                                    <ImageIcon className="size-5" />
                                </span>
                            )}
                        </span>
                        <span className="truncate text-sm font-medium text-foreground">{b.name}</span>
                        {typeof b.pinCount === 'number' && (
                            <span className="-mt-1 text-xs text-muted-foreground">{t('PINTEREST.PIN_COUNT', { count: b.pinCount })}</span>
                        )}
                    </button>
                ))}
            </div>
            <LoadMore show={state.hasMore} loading={state.loadingMore} onClick={state.loadMore} />
        </>
    );
};

const PinList: React.FC<{
    state: PagedState<PinterestPin>;
    busy: string | null;
    onPick: (pin: PinterestPin) => void;
    emptyTitle: string;
}> = ({ state, busy, onPick, emptyTitle }) => {
    const { t } = useTranslation();
    if (state.loading) return <PinSkeleton />;
    if (state.error && !state.items.length) {
        return isPinterestConnectionError(state.error) ? <PinSkeleton /> : <ListError onRetry={state.reload} />;
    }
    if (!state.items.length) return <EmptyList title={emptyTitle} body={t('PINTEREST.EMPTY_PINS_BODY')} />;

    return (
        <>
            {/* Masonry by CSS columns: pins keep their own shape, the way Pinterest shows them. */}
            <div className="columns-2 gap-2 sm:columns-3" data-testid="pinterest-pins">
                {state.items.map((pin) => (
                    <PinTile
                        key={pin.id}
                        pin={pin}
                        busy={busy === pin.id}
                        disabled={Boolean(busy) && busy !== pin.id}
                        onPick={() => onPick(pin)}
                    />
                ))}
            </div>
            <LoadMore show={state.hasMore} loading={state.loadingMore} onClick={state.loadMore} />
        </>
    );
};

const PinTile: React.FC<{ pin: PinterestPin; busy: boolean; disabled: boolean; onPick: () => void }> = ({ pin, busy, disabled, onPick }) => {
    const { t } = useTranslation();
    const title = pin.title || t('PINTEREST.PIN_UNTITLED');
    const ratio = pin.width && pin.height ? `${pin.width} / ${pin.height}` : undefined;

    return (
        <button
            type="button"
            onClick={onPick}
            disabled={disabled || busy}
            title={title}
            aria-label={pin.kind === 'video' ? `${title} · ${t('PINTEREST.VIDEO')}` : title}
            className="group relative mb-2 block w-full break-inside-avoid overflow-hidden rounded-lg border bg-muted text-start outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-default"
            style={ratio ? { aspectRatio: ratio } : undefined}
        >
            {pin.imageUrl ? (
                <img
                    src={pin.imageUrl}
                    alt=""
                    loading="lazy"
                    className={cn('size-full object-cover transition-opacity', disabled && 'opacity-50')}
                />
            ) : (
                <span className="flex aspect-square w-full items-center justify-center text-muted-foreground">
                    <ImageIcon className="size-5" />
                </span>
            )}

            {pin.kind === 'video' && (
                <span className="absolute start-2 top-2 inline-flex items-center gap-1 rounded-md bg-background/90 px-1.5 py-0.5 text-[11px] font-medium text-foreground">
                    <Play className="size-3 fill-current" />
                    {t('PINTEREST.VIDEO')}
                </span>
            )}

            {pin.title && !busy && (
                <span className="absolute inset-x-2 bottom-2 truncate rounded-md bg-background/90 px-2 py-1 text-xs text-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    {pin.title}
                </span>
            )}

            {busy && (
                <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-background/70 text-xs font-medium text-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    {t('PINTEREST.IMPORTING')}
                </span>
            )}
        </button>
    );
};
